import { format, isSameDay, isSameMonth, isToday } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '~shared/shadcn/ui/button';
import { cn } from '~shared/shadcn/lib/utils';

import {
  EVENT_CALENDAR_WEEKDAY_LABELS,
  buildEventCalendarAgendaSections,
  buildEventCalendarDaySections,
  formatEventCalendarTimeRange,
  formatEventCalendarTitle,
  getEventCalendarDaysInRange,
  getEventCalendarRange,
  shiftEventCalendarDate,
} from './event-calendar.helpers';
import {
  EVENT_CALENDAR_VIEW,
  type EventCalendarDaySection,
  type EventCalendarItem,
  type EventCalendarView,
  type MobileEventCalendarProps,
} from './event-calendar.types';

const VIEW_OPTIONS = [
  { id: EVENT_CALENDAR_VIEW.MONTH, label: 'Month' },
  { id: EVENT_CALENDAR_VIEW.WEEK, label: 'Week' },
  { id: EVENT_CALENDAR_VIEW.DAY, label: 'Day' },
  { id: EVENT_CALENDAR_VIEW.AGENDA, label: 'Agenda' },
] as const;

function EventCalendarEventCard({ event }: { event: EventCalendarItem }) {
  return (
    <article className='flex min-w-0 gap-3 rounded-xl border border-border/50 p-3'>
      <div
        className='w-1 shrink-0 rounded-full'
        style={{ backgroundColor: event.color || '#e5e5e5' }}
      />
      <div className='min-w-0'>
        <p className='break-words text-sm font-medium leading-5'>
          {event.title}
        </p>
        <p className='mt-0.5 text-sm text-muted-foreground'>
          {formatEventCalendarTimeRange(event.start, event.end)}
        </p>
      </div>
    </article>
  );
}

function EventCalendarDayList({
  sections,
  emptyLabel,
}: {
  sections: Array<EventCalendarDaySection>;
  emptyLabel: string;
}) {
  if (sections.length === 0) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className='space-y-5'>
      {sections.map((section) => (
        <section key={section.day.toISOString()}>
          <h3 className='mb-2 text-sm font-semibold'>
            {format(section.day, 'EEEE, MMM d')}
          </h3>
          {section.events.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No events</p>
          ) : (
            <div className='space-y-2'>
              {section.events.map((event) => (
                <EventCalendarEventCard
                  key={`${event.resourceId}-${event.start.toISOString()}`}
                  event={event}
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function EventCalendarMonthGrid({
  date,
  selectedDay,
  eventsByDay,
  onSelectDay,
}: {
  date: Date;
  selectedDay: Date;
  eventsByDay: Map<string, Array<EventCalendarItem>>;
  onSelectDay: (day: Date) => void;
}) {
  const range = getEventCalendarRange(EVENT_CALENDAR_VIEW.MONTH, date);
  const days = getEventCalendarDaysInRange(range);

  return (
    <div>
      <div className='mb-2 grid grid-cols-7'>
        {EVENT_CALENDAR_WEEKDAY_LABELS.map((label, index) => (
          <p
            key={`${label}-${index}`}
            className='py-1 text-center text-xs font-medium text-muted-foreground'
          >
            {label}
          </p>
        ))}
      </div>
      <div className='grid grid-cols-7 gap-1'>
        {days.map((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          const dayEvents = eventsByDay.get(dayKey) ?? [];
          const selected = isSameDay(day, selectedDay);
          const inMonth = isSameMonth(day, date);
          const eventCountLabel =
            dayEvents.length === 1
              ? '1 event'
              : `${dayEvents.length} events`;

          return (
            <button
              key={day.toISOString()}
              type='button'
              aria-label={`${format(day, 'MMMM d, yyyy')}${
                dayEvents.length > 0 ? `, ${eventCountLabel}` : ', no events'
              }`}
              aria-pressed={selected}
              onClick={() => {
                onSelectDay(day);
              }}
              className={cn(
                'flex min-h-11 flex-col items-center justify-center rounded-lg px-0.5 py-1 text-sm',
                !inMonth && 'text-muted-foreground',
                isToday(day) && !selected && 'ring-1 ring-secondary',
                selected && 'bg-secondary text-secondary-foreground',
              )}
            >
              <span className='leading-none'>{format(day, 'd')}</span>
              <span className='mt-1 flex h-2 items-center justify-center gap-0.5'>
                {dayEvents.length > 0 ? (
                  <>
                    {dayEvents.slice(0, 3).map((event, index) => (
                      <span
                        key={`${event.resourceId}-${index}`}
                        className={cn(
                          'size-1.5 shrink-0 rounded-full bg-primary shadow-sm',
                          selected && 'ring-1 ring-primary-foreground/30',
                        )}
                        aria-hidden
                      />
                    ))}
                  </>
                ) : (
                  <span className='size-1.5 shrink-0' aria-hidden />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MobileEventCalendar({
  view,
  date,
  events,
  onViewChange,
  onDateChange,
}: MobileEventCalendarProps) {
  const [selectedDay, setSelectedDay] = useState(date);

  useEffect(() => {
    if (view !== EVENT_CALENDAR_VIEW.MONTH) {
      return;
    }

    setSelectedDay((previousDay) =>
      isSameMonth(previousDay, date) ? previousDay : date,
    );
  }, [view, date]);

  const range = getEventCalendarRange(view, date);
  const title = formatEventCalendarTitle(view, date);

  const monthEventsByDay = useMemo(() => {
    if (view !== EVENT_CALENDAR_VIEW.MONTH) {
      return null;
    }
    const monthRange = getEventCalendarRange(EVENT_CALENDAR_VIEW.MONTH, date);
    const days = getEventCalendarDaysInRange(monthRange);
    const sections = buildEventCalendarDaySections(days, events);
    return new Map(
      sections.map((section) => [
        format(section.day, 'yyyy-MM-dd'),
        section.events,
      ]),
    );
  }, [view, date, events]);

  const sections = useMemo((): Array<EventCalendarDaySection> => {
    switch (view) {
      case EVENT_CALENDAR_VIEW.MONTH: {
        const dayKey = format(selectedDay, 'yyyy-MM-dd');
        return [
          {
            day: selectedDay,
            events: monthEventsByDay?.get(dayKey) ?? [],
          },
        ];
      }
      case EVENT_CALENDAR_VIEW.WEEK:
        return buildEventCalendarDaySections(
          getEventCalendarDaysInRange(range),
          events,
        );
      case EVENT_CALENDAR_VIEW.DAY:
        return buildEventCalendarDaySections([date], events);
      case EVENT_CALENDAR_VIEW.AGENDA:
        return buildEventCalendarAgendaSections(events);
      default:
        return [];
    }
  }, [view, date, events, range, selectedDay, monthEventsByDay]);

  const emptyLabel =
    view === EVENT_CALENDAR_VIEW.AGENDA
      ? 'No events in this month.'
      : 'No events on this day.';

  return (
    <div className='flex min-w-0 flex-col gap-4'>
      <div className='flex justify-center gap-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => {
            const today = new Date();
            onDateChange(today);
            setSelectedDay(today);
          }}
        >
          Today
        </Button>
        <Button
          variant='outline'
          size='sm'
          onClick={() => {
            onDateChange(shiftEventCalendarDate(date, view, -1));
          }}
        >
          Back
        </Button>
        <Button
          variant='outline'
          size='sm'
          onClick={() => {
            onDateChange(shiftEventCalendarDate(date, view, 1));
          }}
        >
          Next
        </Button>
      </div>

      <p className='text-center text-sm font-medium'>{title}</p>

      <div className='grid grid-cols-4 gap-2'>
        {VIEW_OPTIONS.map((option) => (
          <Button
            key={option.id}
            variant={view === option.id ? 'default' : 'outline'}
            size='sm'
            className='px-0'
            onClick={() => {
              onViewChange(option.id);
            }}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {view === EVENT_CALENDAR_VIEW.MONTH && monthEventsByDay ? (
        <EventCalendarMonthGrid
          date={date}
          selectedDay={selectedDay}
          eventsByDay={monthEventsByDay}
          onSelectDay={(day) => {
            setSelectedDay(day);
            if (!isSameMonth(day, date)) {
              onDateChange(day);
            }
          }}
        />
      ) : null}

      <EventCalendarDayList sections={sections} emptyLabel={emptyLabel} />
    </div>
  );
}
